import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { MUSIC_VIDEO_CUTS_PER_SONG } from 'fym-shared/src/music24/musicVideoDoc';
import { useShowToast } from '../../../redux/slices/toasts/hooks';
import { editorMessages } from '../messages/editorMessages';
import { stateMessages } from '../messages/stateMessages';
import { useCreateMusicVideoCutMutation } from '../musicVideoEditorApi';
import { SAVE_STATE_MESSAGES } from '../topbar/saveStateMessages';
import { cutCreateError } from './musicVideoErrors';
import { isCutSaved, type CutSaveState } from './useCutAutosave';

interface UseCutActionsProps {
  accountId: number;
  catalogItemUuid: string;
  cutUuid: string;
  flush: () => Promise<CutSaveState>;
  onOpenCut: (cutUuid: string) => void;
}

/**
 * The cut menu actions (design b1): "New cut" (`POST /cuts` with no body: the next number, the
 * director makes it, no credits) and "Copy this cut" (`{ fromCutUuid }`). The open cut saves first;
 * the new cut opens. A copy reads the stored document, so a cut that does not save is not copied:
 * the toast says the save state. A song with the most cuts (`M24_CUT_LIMIT`) gets its own sentence.
 */
export function useCutActions({ accountId, catalogItemUuid, cutUuid, flush, onOpenCut }: UseCutActionsProps) {
  const intl = useIntl();
  const toast = useShowToast();
  const [createCut] = useCreateMusicVideoCutMutation();
  const [isBusy, setIsBusy] = useState(false);

  const create = useCallback(
    async (fromCutUuid?: string): Promise<void> => {
      setIsBusy(true);
      try {
        const saveState = await flush();
        if (fromCutUuid !== undefined && !isCutSaved(saveState)) {
          toast.showError(intl.formatMessage(SAVE_STATE_MESSAGES[saveState]));
          return;
        }
        const { cut } = await createCut({ accountId, catalogItemUuid, fromCutUuid }).unwrap();
        onOpenCut(cut.uuid);
      } catch (error: unknown) {
        console.error('Music video editor: the new cut was not made', error);
        toast.showError(
          cutCreateError(error) === 'limit'
            ? intl.formatMessage(stateMessages.cutLimit, { count: MUSIC_VIDEO_CUTS_PER_SONG })
            : intl.formatMessage(editorMessages.cutFailed)
        );
      } finally {
        setIsBusy(false);
      }
    },
    [accountId, catalogItemUuid, createCut, flush, intl, onOpenCut, toast]
  );

  const newCut = useCallback(() => {
    create().catch(console.error);
  }, [create]);
  const copyCut = useCallback(() => {
    create(cutUuid).catch(console.error);
  }, [create, cutUuid]);
  const openCut = useCallback(
    (uuid: string) => {
      if (uuid === cutUuid) {
        return;
      }
      flush()
        .catch(console.error)
        .finally(() => onOpenCut(uuid));
    },
    [cutUuid, flush, onOpenCut]
  );

  return { newCut, copyCut, openCut, isBusy };
}
